/**
 * Sticky identity header for the dossier (DR-1, 2026-05-13).
 *
 * Lives at the top of FullSheet's scrollable content and stays pinned
 * across tab swaps (sticky CSS, not JS scroll-tracking). Renders the
 * Pokémon's at-a-glance identity:
 *
 *   - 80×80 sprite medallion (BW animated where available; static fallback)
 *     with `filter: saturate(0.35)` desaturation when the live-context
 *     Pokémon is dead (death JOIN hit) per SR-2.
 *   - Name in DM Sans 22/700 + inline gender glyph in 16/600.
 *   - Level chip in DM Mono 13/500.
 *   - HP pill (60×6, threshold-colored) ONLY for live-mode alive members.
 *   - "Not picked" badge for skipped offers (DR-4A; mutually exclusive
 *     with death caption per DR-14 — caller enforces).
 *   - Type chips on their own row (NOT inline — DR-1 anti-wrap on dual-
 *     type at iPhone SE 375 px).
 *   - Nature + ability one-liner DM Sans 12/500 `--text-secondary`,
 *     truncate-no-wrap with ellipsis.
 *   - Death caption (DM Mono 9/400 `--text-tertiary`) — `☠ {diedTo} ·
 *     {diedZone} · Lv {levelAtDeath}` — for live-with-death-JOIN and
 *     career-snapshot kinds.
 *
 * Per SR-3, live mode captures data at modal-open + never re-renders.
 * This component is a stateless factory; the caller derives the input
 * options once at FullSheet.create() time. PR 4b.2 will add a fade
 * helper via the FullSheet.getRootElement() accessor — that's the
 * surface that needs reactive transform, not this header.
 *
 * Pure DOM. No Phaser dependency. Styling lives in
 * `apps/game/src/styles/tokens.css` under `.dossier-identity-header`.
 */

import type { Gender } from '../state/runState';
import { thresholdFor } from './hpBarColors';

/** Display-only info the death caption stitches together. */
export interface DeathSummary {
  /** "wild Pidgey", "Brock's Onix", … — pre-formatted by caller. */
  diedTo: string;
  /** "Route 1", "Pewter Gym", … — pre-formatted by caller. */
  diedZone: string;
  /** Level the Pokémon was at when it fainted. */
  levelAtDeath: number;
}

export interface DossierIdentityHeaderOptions {
  /** Display name ("Bulbasaur"). */
  name: string;
  /** Cartridge gender — drives glyph rendering. */
  gender: Gender;
  /** Level to render in the chip. Live = current PartyMember.level;
   *  snapshot = original offer.level; career-snapshot = levelAtDeath
   *  (caller picks; this primitive renders whatever number it's given). */
  level: number;
  /** 1- or 2-element type list ("Grass" / ["Grass", "Poison"]). */
  types: readonly string[];
  /** Pre-formatted nature line, e.g. "BOLD +DEF / -ATK". */
  natureLine: string;
  /** Pre-formatted ability one-liner. Truncated with ellipsis at CSS layer. */
  abilityLine: string;
  /** Sprite asset URL (BW animated GIF preferred; static PNG fallback OK).
   *  Undefined → renders an empty medallion at `--glass-fill-thin` BG. */
  spriteUrl?: string;
  /** Live HP captured at modal-open. Renders the HP pill when non-null;
   *  hidden in snapshot / career-snapshot / skipped modes. */
  hp?: { current: number; max: number } | null;
  /** Death JOIN result. When non-null, renders ☠ caption + applies
   *  sprite desaturation. Live-context callers populate this from
   *  careerStore.findById(); snapshot-context CAREER callers populate
   *  from the careerEntry's death fields. Mutually exclusive with
   *  `notPicked` (DR-14 — caller enforces). */
  death?: DeathSummary | null;
  /** Skipped-offer "Not picked" badge (DR-4 / DR-14). Mutually exclusive
   *  with `death`. */
  notPicked?: boolean;
}

/** Inline gender glyph + color. Genderless renders em-dash to be visible
 *  alongside the name (not omitted — DESIGN.md gender-glyph vocab). */
function appendGenderGlyph(parent: HTMLElement, gender: Gender): void {
  const span = document.createElement('span');
  span.className = 'dossier-id-header__gender';
  if (gender === 'male') {
    span.textContent = '♂';
    span.style.color = 'rgba(75, 141, 255, 0.70)';
    span.setAttribute('aria-label', 'male');
  } else if (gender === 'female') {
    span.textContent = '♀';
    span.style.color = 'rgba(255, 147, 200, 0.70)';
    span.setAttribute('aria-label', 'female');
  } else {
    span.textContent = '—';
    span.style.color = 'var(--text-tertiary)';
    span.setAttribute('aria-label', 'genderless');
  }
  parent.appendChild(span);
}

function appendTypeChip(parent: HTMLElement, type: string): void {
  const chip = document.createElement('span');
  chip.className = 'dossier-id-header__type-chip';
  chip.style.setProperty('--chip-type', `var(--type-${type.toLowerCase()})`);
  chip.textContent = type;
  parent.appendChild(chip);
}

function appendHpPill(
  parent: HTMLElement,
  hp: { current: number; max: number },
): void {
  const pill = document.createElement('div');
  pill.className = 'dossier-id-header__hp-pill';
  pill.setAttribute('role', 'progressbar');
  pill.setAttribute('aria-valuemin', '0');
  pill.setAttribute('aria-valuemax', String(hp.max));
  pill.setAttribute('aria-valuenow', String(hp.current));
  pill.setAttribute('aria-label', `Hit points: ${hp.current} of ${hp.max}`);

  const fill = document.createElement('div');
  fill.className = 'dossier-id-header__hp-fill';
  const pct = hp.max > 0 ? Math.max(0, Math.min(1, hp.current / hp.max)) : 0;
  fill.style.width = `${Math.round(pct * 100)}%`;
  fill.dataset.threshold = thresholdFor(pct);
  pill.appendChild(fill);
  parent.appendChild(pill);
}

function appendDeathCaption(parent: HTMLElement, death: DeathSummary): void {
  const cap = document.createElement('div');
  cap.className = 'dossier-id-header__death-caption';
  cap.textContent = `☠ ${death.diedTo} · ${death.diedZone} · Lv ${death.levelAtDeath}`;
  parent.appendChild(cap);
}

function appendNotPickedBadge(parent: HTMLElement): void {
  const badge = document.createElement('span');
  badge.className = 'dossier-id-header__not-picked';
  badge.textContent = 'NOT PICKED';
  parent.appendChild(badge);
}

/**
 * Build the `.dossier-identity-header` element. Returns an unparented
 * container the caller mounts at the top of FullSheet's scrollable
 * content; CSS `position: sticky; top: 0` keeps it pinned across tab
 * swaps + content scroll.
 */
export function createDossierIdentityHeader(
  opts: DossierIdentityHeaderOptions,
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'dossier-identity-header';
  if (opts.death) root.dataset.dead = 'true';

  // Sprite medallion (desaturate on dead via CSS [data-dead="true"]
  // descendant selector).
  const medallion = document.createElement('div');
  medallion.className = 'dossier-id-header__medallion';
  if (opts.spriteUrl) {
    const img = document.createElement('img');
    img.className = 'dossier-id-header__sprite';
    img.src = opts.spriteUrl;
    img.alt = ''; // decorative — name is in the heading
    medallion.appendChild(img);
  }
  root.appendChild(medallion);

  const body = document.createElement('div');
  body.className = 'dossier-id-header__body';

  // Top row: name + gender glyph + level chip + (HP pill | Not Picked badge).
  const topRow = document.createElement('div');
  topRow.className = 'dossier-id-header__top-row';
  const heading = document.createElement('h2');
  heading.className = 'dossier-id-header__name';
  heading.textContent = opts.name;
  appendGenderGlyph(heading, opts.gender);
  topRow.appendChild(heading);

  const levelChip = document.createElement('span');
  levelChip.className = 'dossier-id-header__level';
  levelChip.textContent = `Lv ${opts.level}`;
  topRow.appendChild(levelChip);

  if (opts.hp) {
    appendHpPill(topRow, opts.hp);
  } else if (opts.notPicked) {
    appendNotPickedBadge(topRow);
  }
  body.appendChild(topRow);

  // Type chips row (own row — DR-1 anti-wrap on dual-type narrow viewport).
  if (opts.types.length > 0) {
    const typesRow = document.createElement('div');
    typesRow.className = 'dossier-id-header__types';
    for (const t of opts.types) appendTypeChip(typesRow, t);
    body.appendChild(typesRow);
  }

  // Nature + ability one-liner (truncate with ellipsis at CSS layer).
  const tagline = document.createElement('div');
  tagline.className = 'dossier-id-header__tagline';
  const natureSpan = document.createElement('span');
  natureSpan.className = 'dossier-id-header__nature';
  natureSpan.textContent = opts.natureLine;
  tagline.appendChild(natureSpan);
  if (opts.abilityLine) {
    const sep = document.createElement('span');
    sep.className = 'dossier-id-header__tagline-sep';
    sep.textContent = ' · ';
    tagline.appendChild(sep);
    const abilSpan = document.createElement('span');
    abilSpan.className = 'dossier-id-header__ability';
    abilSpan.textContent = opts.abilityLine;
    tagline.appendChild(abilSpan);
  }
  body.appendChild(tagline);

  // Death caption (career-snapshot or live-with-JOIN-dead).
  if (opts.death) {
    appendDeathCaption(body, opts.death);
  }

  root.appendChild(body);
  return root;
}
